import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Cancel as RevokeIcon,
  Send as ResendIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { systemAdminInviteService } from '../../services/systemAdminInviteService';
import { SYSTEM_ROLES, getSystemRoleDisplayName } from '../../utils/systemPermissions';
import logger from '../../utils/logger';

const STATUS_COLORS = {
  pending: 'warning',
  accepted: 'success',
  declined: 'default',
  expired: 'error',
  revoked: 'default'
};

/**
 * SystemAdminInviteManager
 * Root Admin component for creating and managing IT professional invitations.
 * Accessed from the System Admin portal.
 */
const SystemAdminInviteManager = () => {
  const { currentUser } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [newInvite, setNewInvite] = useState({
    inviteeEmail: '',
    inviteeName: '',
    systemRole: SYSTEM_ROLES.DEVELOPER,
    customMessage: ''
  });

  // Roles available for invitation (exclude ROOT_ADMIN)
  const invitableRoles = Object.entries(SYSTEM_ROLES)
    .filter(([key]) => key !== 'ROOT_ADMIN')
    .map(([key, value]) => ({
      key,
      value,
      label: getSystemRoleDisplayName(value)
    }));

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await systemAdminInviteService.getAllInvitations();
      setInvitations(data);
    } catch (err) {
      logger.error('Error loading invitations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const handleCreateInvitation = async () => {
    if (!newInvite.inviteeEmail.trim()) {
      setSnackbar({ open: true, message: 'Email is required', severity: 'error' });
      return;
    }

    try {
      setCreating(true);
      const result = await systemAdminInviteService.createInvitation(
        currentUser.uid,
        newInvite
      );

      setSnackbar({
        open: true,
        message: `Invitation created! Link: ${result.inviteLink}`,
        severity: 'success'
      });

      // Copy link to clipboard
      try {
        await navigator.clipboard.writeText(result.inviteLink);
      } catch {
        // Clipboard may not be available
      }

      setDialogOpen(false);
      setNewInvite({
        inviteeEmail: '',
        inviteeName: '',
        systemRole: SYSTEM_ROLES.DEVELOPER,
        customMessage: ''
      });

      loadInvitations();
    } catch (err) {
      logger.error('Error creating invitation:', err);
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (inviteCode) => {
    const link = `${window.location.origin}/system-join/${inviteCode}`;
    try {
      await navigator.clipboard.writeText(link);
      setSnackbar({ open: true, message: 'Invitation link copied to clipboard', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: `Link: ${link}`, severity: 'info' });
    }
  };

  const handleRevoke = async (inviteCode) => {
    try {
      await systemAdminInviteService.revokeInvitation(inviteCode, currentUser.uid);
      setSnackbar({ open: true, message: 'Invitation revoked', severity: 'success' });
      loadInvitations();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleResend = async (inviteCode) => {
    try {
      await systemAdminInviteService.resendInvitation(inviteCode, currentUser.uid);
      setSnackbar({ open: true, message: 'Invitation resent (expiration extended)', severity: 'success' });
      loadInvitations();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">IT Professional Invitations</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInvitations}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Create Invitation
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          IT Professional accounts are completely separate from family accounts.
          An IT professional can later create a separate family account using the same email if they wish.
          System admin invitations expire after 7 days.
        </Typography>
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : invitations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No system admin invitations yet. Click "Create Invitation" to invite an IT professional.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name / Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {inv.inviteeName || 'Not specified'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {inv.inviteeEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getSystemRoleDisplayName(inv.systemRole)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={inv.status}
                      size="small"
                      color={STATUS_COLORS[inv.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(inv.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(inv.expiresAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {inv.status === 'pending' && (
                      <>
                        <Tooltip title="Copy invitation link">
                          <IconButton size="small" onClick={() => handleCopyLink(inv.inviteCode)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke invitation">
                          <IconButton size="small" color="error" onClick={() => handleRevoke(inv.inviteCode)}>
                            <RevokeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(inv.status === 'expired' || inv.status === 'pending') && (
                      <Tooltip title="Resend (extend expiration)">
                        <IconButton size="small" color="primary" onClick={() => handleResend(inv.inviteCode)}>
                          <ResendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Invitation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite IT Professional</DialogTitle>
        <DialogContent>
          <TextField
            required
            fullWidth
            label="Email Address"
            value={newInvite.inviteeEmail}
            onChange={(e) => setNewInvite(prev => ({ ...prev, inviteeEmail: e.target.value }))}
            margin="normal"
            type="email"
          />
          <TextField
            fullWidth
            label="Full Name (Optional)"
            value={newInvite.inviteeName}
            onChange={(e) => setNewInvite(prev => ({ ...prev, inviteeName: e.target.value }))}
            margin="normal"
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel>System Role</InputLabel>
            <Select
              value={newInvite.systemRole}
              onChange={(e) => setNewInvite(prev => ({ ...prev, systemRole: e.target.value }))}
              label="System Role"
            >
              {invitableRoles.map(role => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Welcome Message (Optional)"
            value={newInvite.customMessage}
            onChange={(e) => setNewInvite(prev => ({ ...prev, customMessage: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateInvitation}
            disabled={creating || !newInvite.inviteeEmail.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create & Copy Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemAdminInviteManager;
